import CollisionManager from './CollisionManager.js';
import GhostPreview from './GhostPreview.js';
import AddTool from './tools/AddTool.js';
import MoveTool from './tools/MoveTool.js';
import ExtendTool from './tools/ExtendTool.js';
import Material from '../materials/Material.js';
import TexturePicker from '../ui/widgets/TexturePicker.js';
import { ANIMATED_PRESETS } from '../materials/AnimatedPresets.js';

/**
 * Main voxel editor controller
 * Manages tools, UI, and integrates with the world
 */
export default class VoxelEditor {
  /**
   * Create a new voxel editor
   * @param {World} world - The world instance
   * @param {Camera} camera - The camera instance
   * @param {InputManager} inputManager - The input manager instance
   * @param {HTMLElement} container - Container element to render into (from SidebarManager)
   */
  constructor(world, camera, inputManager, container) {
    this.world = world;
    this.camera = camera;
    this.inputManager = inputManager;
    this.container = container;

    // Create subsystems
    this.collisionManager = new CollisionManager(world);
    this.ghostPreview = new GhostPreview(world);

    // Create tools
    this.tools = {
      add: new AddTool(this),
      move: new MoveTool(this),
      extend: new ExtendTool(this)
    };

    // Current state
    this.activeTool = null;
    this.enabled = false;

    // Material presets for Add tool
    this.materialPresets = this.createMaterialPresets();
    this.selectedPreset = null; // Current preset (key from materialPresets, colors only)
    this.selectedTexturePath = 'textures/brick/red_brick_1.webp'; // Default: Red Brick 1
    this._animatedOpacity = null; // Opacity override for animated presets
    this.useTriplanar = true; // Group all 6 faces with triplanar projection
    this.useNoCollision = false; // Materials don't block movement (water, gas, etc.)

    // Mobile long press state
    this.longPressTimer = null;
    this.longPressDelay = 400; // ms to wait before starting drag on mobile
    this.longPressTouchStart = null; // { x, y, target, faceEl }
    this.touchWasCameraDrag = false; // Track if touch was used for camera movement

    // UI elements
    this.toolButtons = {};

    // Set up UI and events
    this.createUI();
    this.setupEventListeners();

    // Listen for toolbar submenu changes — only enable for cube submenu
    window.addEventListener('toolbar-submenu-change', (e) => {
      if (e.detail.level === 'cube') {
        this.enable();
      } else if (this.enabled) {
        this.disable();
      }
    });
  }

  /**
   * Create material presets for the Add tool
   * @return {Object} Preset definitions
   */
  createMaterialPresets() {
    return {
      // Colors
      red: {
        name: 'Red',
        type: 'color',
        color: '#e74c3c',
        preview: '#e74c3c'
      },
      blue: {
        name: 'Blue',
        type: 'color',
        color: '#3498db',
        preview: '#3498db'
      },
      green: {
        name: 'Green',
        type: 'color',
        color: '#2ecc71',
        preview: '#2ecc71'
      },
      yellow: {
        name: 'Yellow',
        type: 'color',
        color: '#f1c40f',
        preview: '#f1c40f'
      },
      purple: {
        name: 'Purple',
        type: 'color',
        color: '#9b59b6',
        preview: '#9b59b6'
      },
      orange: {
        name: 'Orange',
        type: 'color',
        color: '#e67e22',
        preview: '#e67e22'
      },
      white: {
        name: 'White',
        type: 'color',
        color: '#ffffff',
        preview: '#ffffff'
      },
      black: {
        name: 'Black',
        type: 'color',
        color: '#1a1a1a',
        preview: '#1a1a1a'
      }
    };
  }

  /**
   * Create a material from a preset
   * @param {string} presetKey - Key from materialPresets
   * @return {Material} The material instance
   */
  createMaterialFromPreset(presetKey) {
    const preset = this.materialPresets[presetKey];
    if (!preset) return null;

    if (preset.type === 'color') {
      return Material.color(preset.color);
    }
    return null;
  }

  /**
   * Create a material from the currently selected source (texture or color preset)
   * @returns {Material|null}
   */
  createSelectedMaterial() {
    let material = null;

    if (this.selectedTexturePath) {
      const opacity = this._animatedOpacity != null ? this._animatedOpacity : 1.0;
      material = Material.texture(this.selectedTexturePath, opacity);
    } else if (this.selectedPreset) {
      material = this.createMaterialFromPreset(this.selectedPreset);
    }

    // Apply noCollision setting if enabled
    if (material && this.useNoCollision) {
      material.noCollision = true;
    }

    return material;
  }

  /**
   * Get the name of the currently selected material
   * @returns {string}
   */
  getSelectedMaterialName() {
    if (this.selectedTexturePath) {
      // Extract name from path: "textures/brick/adobe_brick_1.webp" -> "Adobe Brick 1"
      const filename = this.selectedTexturePath.split('/').pop().replace(/\.\w+$/, '');
      return filename.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    }
    if (this.selectedPreset) {
      return this.materialPresets[this.selectedPreset]?.name || 'Unknown';
    }
    return 'None';
  }

  /**
   * Get materials object for all faces from current selection (texture or color preset)
   * @return {Object|null} Materials for each face, or null if nothing selected
   */
  getPresetMaterials() {
    if (!this.selectedPreset && !this.selectedTexturePath) return null;

    const material = this.createSelectedMaterial();
    if (!material) return null;

    // Create a material for each face (cloned to avoid shared state)
    return {
      top: this.createSelectedMaterial(),
      bottom: this.createSelectedMaterial(),
      front: this.createSelectedMaterial(),
      back: this.createSelectedMaterial(),
      left: this.createSelectedMaterial(),
      right: this.createSelectedMaterial()
    };
  }

  /**
   * Set the selected color preset (clears texture selection)
   * @param {string|null} presetKey - Preset key or null to clear
   */
  setPreset(presetKey) {
    this.selectedPreset = presetKey;

    // Clear texture selection when selecting a color
    if (presetKey) {
      this.selectedTexturePath = null;
      this._animatedOpacity = null;
      if (this.texturePicker) {
        this.texturePicker.setSelection(null);
      }
      if (this._animatedPresetButtons) {
        this._animatedPresetButtons.forEach(b => b.classList.remove('active'));
      }
    }

    // Update color preset buttons UI
    if (this.presetButtons) {
      Object.keys(this.presetButtons).forEach(key => {
        this.presetButtons[key].classList.toggle('active', key === presetKey);
      });
    }

    // Update ghost preview
    this.updateGhostPreview();
    this._updateToolbarSwatch();
  }

  /**
   * Set the selected texture from the TexturePicker (clears color preset selection)
   * @param {string|null} texturePath - Texture path or null to clear
   */
  setTexturePreset(texturePath) {
    this.selectedTexturePath = texturePath;

    // Clear color preset selection when selecting a texture
    if (texturePath) {
      this.selectedPreset = null;
      this._animatedOpacity = null;
      if (this.presetButtons) {
        Object.keys(this.presetButtons).forEach(key => {
          this.presetButtons[key].classList.remove('active');
        });
      }
      if (this._animatedPresetButtons) {
        this._animatedPresetButtons.forEach(b => b.classList.remove('active'));
      }
    }

    // Update ghost preview
    this.updateGhostPreview();
    this._updateToolbarSwatch();
  }

  /**
   * Set the selected animated preset (uses texture path mechanism + auto-sets noCollision/opacity)
   * @param {Object|null} preset - An ANIMATED_PRESETS entry or null to clear
   */
  setAnimatedPreset(preset) {
    if (preset) {
      // Auto-enable noCollision
      this.useNoCollision = preset.noCollision;
      if (this.noCollisionToggle) {
        this.noCollisionToggle.checked = preset.noCollision;
      }

      // Use the normal texture path mechanism (this clears _animatedOpacity)
      this.setTexturePreset(preset.texture);
      // Set opacity AFTER setTexturePreset (which clears it)
      this._animatedOpacity = preset.opacity;
      // Update opacity slider UI
      if (this.opacitySlider) {
        this.opacitySlider.value = preset.opacity;
        this.opacityValue.textContent = preset.opacity.toFixed(2);
      }
    } else {
      this._animatedOpacity = null;
      // Reset opacity slider to 1.0
      if (this.opacitySlider) {
        this.opacitySlider.value = 1;
        this.opacityValue.textContent = '1.00';
      }
      if (this._animatedPresetButtons) {
        this._animatedPresetButtons.forEach(b => b.classList.remove('active'));
      }
    }
  }

  /**
   * Update the ghost preview with current selection
   */
  updateGhostPreview() {
    if (this.activeTool?.name === 'add') {
      const material = this.createSelectedMaterial();
      this.ghostPreview.setPresetMaterial(material, this.useTriplanar);
    }
  }

  /**
   * Update the toolbar swatch button to reflect the current material selection
   */
  _updateToolbarSwatch() {
    if (!this.sidebarManager) return;
    if (this.selectedTexturePath) {
      // GIFs (animated presets) don't have thumbs — use the file directly
      const isGif = this.selectedTexturePath.endsWith('.gif');
      const thumbPath = isGif ? this.selectedTexturePath : this.selectedTexturePath.replace('textures/', 'textures/thumbs/');
      this.sidebarManager.updateSwatchPreview(null, thumbPath);
    } else if (this.selectedPreset) {
      const preset = this.materialPresets[this.selectedPreset];
      if (preset) {
        this.sidebarManager.updateSwatchPreview(preset.preview, null);
      }
    } else {
      this.sidebarManager.updateSwatchPreview(null, null);
    }
  }

  /**
   * Create a material group for a cuboid with all 6 faces
   * Used when triplanar mode is enabled
   * @param {string} cuboidKey - The cuboid key (x,y,z,w,h,d)
   */
  createTriplanarGroup(cuboidKey) {
    if ((!this.selectedPreset && !this.selectedTexturePath) || !this.useTriplanar) return;

    const groupManager = this.world.materialGroupManager;
    if (!groupManager) return;

    const material = this.createSelectedMaterial();
    if (!material) return;

    // Create a new group with the selected material
    const group = groupManager.createGroup({
      name: `${this.getSelectedMaterialName()} Block`,
      material: material
    });

    // Assign all 6 faces to the group
    const faces = ['top', 'bottom', 'front', 'back', 'left', 'right'];
    const faceAssignments = faces.map(face => ({ cuboidKey, face }));
    groupManager.assignFacesToGroup(group.id, faceAssignments);

    // Apply the triplanar projection
    groupManager.applyGroupMaterial(group.id);
  }

  /**
   * Set the sidebar manager reference
   * @param {SidebarManager} sidebarManager
   */
  setSidebarManager(sidebarManager) {
    this.sidebarManager = sidebarManager;
  }

  /**
   * Enable the editor
   */
  enable() {
    this.enabled = true;
    this.setTool('add'); // Default to add tool
  }

  /**
   * Disable the editor
   */
  disable() {
    this.enabled = false;
    this._stopGlobalDragListeners(); // Clean up any active drag listeners
    this.setTool(null);
  }

  /**
   * Toggle editor on/off via sidebar tab
   */
  toggle() {
    if (this.sidebarManager) {
      if (this.enabled) {
        this.sidebarManager.switchToTab('materials');
      } else {
        this.sidebarManager.switchToTab('edit');
      }
    }
  }

  /**
   * Set the active tool
   * @param {string|null} toolName - Tool name ('add', 'move', 'extend') or null
   */
  setTool(toolName) {
    // Deactivate current tool
    if (this.activeTool) {
      this.activeTool.deactivate();
    }

    // Update button states (toolbar handles highlight via setActiveTool)
    Object.keys(this.toolButtons).forEach(name => {
      if (this.toolButtons[name]) {
        this.toolButtons[name].classList.toggle('active', name === toolName);
      }
    });

    // Notify toolbar of tool change for highlight sync
    if (this.sidebarManager && this.sidebarManager.setActiveTool) {
      this.sidebarManager.setActiveTool(toolName);
    }

    // Activate new tool
    if (toolName && this.tools[toolName]) {
      this.activeTool = this.tools[toolName];
      this.activeTool.activate();
    } else {
      this.activeTool = null;
    }

    // Only show material preview for the Add tool
    if (toolName === 'add' && (this.selectedPreset || this.selectedTexturePath)) {
      this.updateGhostPreview();
    } else {
      this.ghostPreview.setPresetMaterial(null, false);
    }

    // Notify other systems (e.g. ImpostorTool cancels placement when a tool is selected)
    window.dispatchEvent(new CustomEvent('editor-tool-changed', { detail: { tool: toolName } }));
  }

  /**
   * Create the editor UI content (renders into container)
   */
  createUI() {
    // Content wrapper — presets only (tool buttons are now in the TopToolbar)
    const content = document.createElement('div');
    content.className = 'gui-content voxel-editor-content';

    // Material Presets section
    const presetsSection = document.createElement('div');
    presetsSection.className = 'gui-section';
    presetsSection.innerHTML = `
      <div class="gui-section-header">
        <span class="gui-section-title">Material Presets</span>
      </div>
      <div class="gui-section-body">
        <div class="preset-group">
          <div class="preset-group-label">Textures</div>
          <div id="texturePickerContainer"></div>
        </div>
        <div class="preset-group">
          <div class="preset-group-label">Colors</div>
          <div class="preset-grid" id="colorPresets"></div>
        </div>
        <div class="preset-group">
          <div class="preset-group-label">Animated</div>
          <div class="preset-grid" id="animatedPresets"></div>
        </div>
        <div class="preset-option">
          <label class="gui-checkbox-container">
            <input type="checkbox" class="gui-checkbox" id="triplanarToggle" checked>
            <span class="gui-checkbox-label">Triplanar (group all faces)</span>
          </label>
        </div>
        <div class="preset-option">
          <label class="gui-checkbox-container">
            <input type="checkbox" class="gui-checkbox" id="noCollisionToggle">
            <span class="gui-checkbox-label">No Collision (water/gas)</span>
          </label>
        </div>
        <div class="preset-option">
          <label class="gui-slider-label">Opacity</label>
          <input type="range" class="gui-slider" id="opacitySlider" min="0" max="1" step="0.05" value="1">
          <span class="gui-slider-value" id="opacityValue">1.00</span>
        </div>
        <button class="preset-clear-btn" id="clearPreset">Clear Selection</button>
      </div>
    `;

    content.appendChild(presetsSection);

    // Add to container (now a TopSheet content area)
    this.container.appendChild(content);

    // Tool buttons are managed by TopToolbar — keep empty reference for compat
    this.toolButtons = {};

    // Create texture picker
    const texturePickerContainer = content.querySelector('#texturePickerContainer');
    this.texturePicker = new TexturePicker({
      onSelect: (texture) => this.setTexturePreset(texture.path)
    });
    this.texturePicker.loadManifest().then(() => {
      this.texturePicker.render(texturePickerContainer);
      // Pre-select default texture (Red Brick 1)
      if (this.selectedTexturePath) {
        this.texturePicker.setSelection(this.selectedTexturePath);
        this._updateToolbarSwatch();
      }
    });

    // Create color preset buttons
    this.presetButtons = {};
    const colorGrid = content.querySelector('#colorPresets');

    Object.entries(this.materialPresets).forEach(([key, preset]) => {
      const btn = document.createElement('button');
      btn.className = 'preset-btn';
      btn.title = preset.name;
      btn.style.backgroundColor = preset.preview;

      // Add border for light colors
      if (preset.color === '#ffffff' || preset.color === '#f1c40f') {
        btn.style.border = '1px solid #666';
      }
      colorGrid.appendChild(btn);

      btn.addEventListener('click', () => this.setPreset(key));
      this.presetButtons[key] = btn;
    });

    // Animated preset buttons
    const animatedGrid = content.querySelector('#animatedPresets');
    this._animatedPresetButtons = [];
    ANIMATED_PRESETS.forEach(preset => {
      const btn = document.createElement('button');
      btn.className = 'preset-btn preset-btn-animated';
      btn.title = preset.label;
      // Use img element for GIF thumbnail (renders more reliably than background-image)
      const img = document.createElement('img');
      img.src = preset.thumbnail;
      img.alt = preset.label;
      img.style.cssText = 'width:100%;height:100%;object-fit:cover;position:absolute;inset:0;';
      btn.appendChild(img);
      animatedGrid.appendChild(btn);
      btn.addEventListener('click', () => {
        this.setAnimatedPreset(preset);
        // Re-highlight after setAnimatedPreset (which calls setTexturePreset that clears all)
        this._animatedPresetButtons.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
      this._animatedPresetButtons.push(btn);
    });

    // Clear preset button
    const clearBtn = content.querySelector('#clearPreset');
    clearBtn.addEventListener('click', () => {
      this.setPreset(null);
      this.setAnimatedPreset(null);
    });

    // Triplanar toggle
    this.triplanarToggle = content.querySelector('#triplanarToggle');
    this.triplanarToggle.addEventListener('change', (e) => {
      this.useTriplanar = e.target.checked;
      // Update ghost preview with new triplanar setting
      if (this.activeTool?.name === 'add' && (this.selectedPreset || this.selectedTexturePath)) {
        this.ghostPreview.setPresetMaterial(
          this.createSelectedMaterial(),
          this.useTriplanar
        );
      }
    });

    // No Collision toggle
    this.noCollisionToggle = content.querySelector('#noCollisionToggle');
    this.noCollisionToggle.addEventListener('change', (e) => {
      this.useNoCollision = e.target.checked;
    });

    // Opacity slider
    this.opacitySlider = content.querySelector('#opacitySlider');
    this.opacityValue = content.querySelector('#opacityValue');
    this.opacitySlider.addEventListener('input', (e) => {
      const val = parseFloat(e.target.value);
      this._animatedOpacity = val < 1 ? val : null;
      this.opacityValue.textContent = val.toFixed(2);
      this.updateGhostPreview();
    });
  }

  /**
   * Set up event listeners for mouse, touch, and keyboard
   */
  setupEventListeners() {
    const container = document.getElementById('container');

    // Bound handlers for cleanup
    this._boundMouseMove = (e) => this.handleMouseMove(e);
    this._boundMouseUp = (e) => this.handleMouseUp(e);
    this._boundTouchMove = (e) => this.handleTouchMove(e);
    this._boundTouchEnd = (e) => this.handleTouchEnd(e);

    // Mouse down - capture phase to intercept before InputManager
    container.addEventListener('mousedown', (e) => this.handleMouseDown(e), true);

    // Mouse move - for hover preview (e.g., AddTool ghost)
    container.addEventListener('mousemove', (e) => this.handleMouseMove(e));

    // Touch events - capture phase to intercept before InputManager
    // Need all three events to properly detect and handle camera drags
    container.addEventListener('touchstart', (e) => this.handleTouchStart(e), true);
    container.addEventListener('touchmove', (e) => this.handleTouchMove(e), { capture: true, passive: false });
    container.addEventListener('touchend', (e) => this.handleTouchEnd(e), true);
    container.addEventListener('touchcancel', (e) => this.handleTouchEnd(e), true);

    // Keyboard shortcuts
    window.addEventListener('keydown', (e) => this.handleKeyDown(e));

    // Handle window blur to cancel drags if window loses focus
    window.addEventListener('blur', () => {
      if (this.activeTool && this.activeTool.isDragging) {
        this.activeTool.cancelDrag?.();
        this._stopGlobalDragListeners();
      }
    });
  }

  /**
   * Add global mouse/touch listeners when dragging starts
   * This allows drags to continue over UI elements and outside the window
   */
  _startGlobalDragListeners(isTouch = false) {
    if (isTouch) {
      window.addEventListener('touchmove', this._boundTouchMove, { capture: true, passive: false });
      window.addEventListener('touchend', this._boundTouchEnd, true);
      window.addEventListener('touchcancel', this._boundTouchEnd, true);
    } else {
      window.addEventListener('mousemove', this._boundMouseMove, true);
      window.addEventListener('mouseup', this._boundMouseUp, true);
    }
    this._usingTouchListeners = isTouch;
  }

  /**
   * Remove global mouse/touch listeners when dragging ends
   */
  _stopGlobalDragListeners() {
    window.removeEventListener('mousemove', this._boundMouseMove, true);
    window.removeEventListener('mouseup', this._boundMouseUp, true);
    window.removeEventListener('touchmove', this._boundTouchMove, true);
    window.removeEventListener('touchend', this._boundTouchEnd, true);
    window.removeEventListener('touchcancel', this._boundTouchEnd, true);
    this._usingTouchListeners = false;
  }

  /**
   * Handle mouse down events
   * @param {MouseEvent} event
   */
  handleMouseDown(event) {
    if (!this.enabled || !this.activeTool) return;

    // Only intercept left-click when tool is active
    if (event.button !== 0) return;

    // Don't intercept if clicking on UI
    if (event.target.closest('.top-toolbar')) return;
    if (event.target.closest('.top-sheet')) return;
    if (event.target.closest('.settings-panel')) return;
    if (event.target.closest('.info-panel')) return;
    if (event.target.closest('.coords')) return;

    // Forward to active tool
    this.activeTool.onMouseDown(event);

    // If tool started dragging, prevent InputManager from handling
    // and set up global listeners so drag continues over UI/outside window
    if (this.activeTool.isDragging) {
      event.stopPropagation();
      this._startGlobalDragListeners(false); // false = mouse, not touch
    }
  }

  /**
   * Handle mouse move events
   * @param {MouseEvent} event
   */
  handleMouseMove(event) {
    if (!this.enabled || !this.activeTool) return;

    // Forward to active tool
    this.activeTool.onMouseMove(event);

    // If tool is dragging, update ghost and stop propagation
    if (this.activeTool.isDragging) {
      event.stopPropagation();
    }
  }

  /**
   * Handle mouse up events
   * @param {MouseEvent} event
   */
  handleMouseUp(event) {
    // Always stop global listeners on mouseup (even if disabled or no tool)
    this._stopGlobalDragListeners();

    if (!this.enabled || !this.activeTool) return;

    // Forward to active tool
    this.activeTool.onMouseUp(event);
  }

  /**
   * Create a pointer-like event object from a touch
   * @param {Touch} touch - The touch object
   * @param {Element} target - The event target
   * @returns {Object} Pointer-like event object
   */
  _createPointerEvent(touch, target) {
    return {
      clientX: touch.clientX,
      clientY: touch.clientY,
      target: target,
      button: 0 // Simulate left click
    };
  }

  /**
   * Cancel any pending long press timer
   */
  _cancelLongPress() {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
    // Remove visual feedback
    if (this.longPressTouchStart?.faceEl) {
      this.longPressTouchStart.faceEl.classList.remove('face-long-press');
    }
    this.longPressTouchStart = null;
  }

  /**
   * Handle touch start events
   * @param {TouchEvent} event
   */
  handleTouchStart(event) {
    // Multi-touch (pinch zoom) - hide ghost and cancel any pending long press
    if (event.touches.length > 1) {
      this.ghostPreview.hide();
      this._cancelLongPress();
      this.touchWasCameraDrag = true; // Prevent selection on touch end
      return;
    }

    // Only handle single-finger touch
    if (event.touches.length !== 1) return;

    const touch = event.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);

    // Reset camera drag flag for new touch
    this.touchWasCameraDrag = false;

    // Don't intercept if touching UI
    if (target?.closest('.sidebar-panel')) return;
    if (target?.closest('.info-panel')) return;
    if (target?.closest('.coords')) return;

    // If editor not enabled or no tool, nothing to do
    if (!this.enabled || !this.activeTool) return;

    // Check if touching a face (for move/extend tools)
    const faceEl = target?.closest('.face');
    if (!faceEl || faceEl.closest('.ghost-preview')) return;

    // Cancel any existing long press
    this._cancelLongPress();

    // Store touch start info for long press detection
    this.longPressTouchStart = {
      x: touch.clientX,
      y: touch.clientY,
      target: target,
      faceEl: faceEl
    };

    // Add visual feedback for long press
    faceEl.classList.add('face-long-press');

    // Start long press timer - editing begins after delay
    this.longPressTimer = setTimeout(() => {
      if (!this.longPressTouchStart) return;

      const { target } = this.longPressTouchStart;
      const pointerEvent = this._createPointerEvent(
        { clientX: this.longPressTouchStart.x, clientY: this.longPressTouchStart.y },
        target
      );

      // Remove visual feedback
      faceEl.classList.remove('face-long-press');
      this.longPressTimer = null;

      // Start the tool action
      this.activeTool.onMouseDown(pointerEvent);

      // If tool started dragging, set up global listeners
      if (this.activeTool.isDragging) {
        this._startGlobalDragListeners(true);
      }
    }, this.longPressDelay);
  }

  /**
   * Handle touch move events
   * @param {TouchEvent} event
   */
  handleTouchMove(event) {
    // Multi-touch (pinch zoom) - hide ghost and mark as camera drag
    if (event.touches.length > 1) {
      this.ghostPreview.hide();
      this._cancelLongPress();
      this.touchWasCameraDrag = true;
      return;
    }

    // Only handle single-finger touch
    if (event.touches.length !== 1) return;

    // ANY movement immediately disables editor for this touch
    // This allows camera panning to take priority
    if (!this.touchWasCameraDrag) {
      this.touchWasCameraDrag = true;
      this._cancelLongPress();
    }

    // If tool is actively dragging (long press completed before movement), continue the drag
    if (this.enabled && this.activeTool?.isDragging) {
      const touch = event.touches[0];
      const pointerEvent = this._createPointerEvent(touch, event.target);
      this.activeTool.onMouseMove(pointerEvent);
      event.stopImmediatePropagation(); // Prevent duplicate handling from multiple listeners
      event.preventDefault();
    }
  }

  /**
   * Handle touch end events
   * @param {TouchEvent} event
   */
  handleTouchEnd(event) {
    // Cancel any pending long press
    this._cancelLongPress();

    // Always stop global listeners on touchend
    this._stopGlobalDragListeners();

    // If this touch was used for camera dragging, prevent synthetic click
    // which would otherwise trigger face selection via FacePicker
    if (this.touchWasCameraDrag) {
      this.touchWasCameraDrag = false;
      event.preventDefault();
      return;
    }

    if (!this.enabled || !this.activeTool) return;

    // Only forward to tool if it was actually dragging
    if (!this.activeTool.isDragging) return;

    // Use last known touch position or create minimal event
    const touch = event.changedTouches?.[0];
    const pointerEvent = touch
      ? this._createPointerEvent(touch, event.target)
      : { clientX: 0, clientY: 0, button: 0, target: event.target };

    // Forward to active tool
    this.activeTool.onMouseUp(pointerEvent);

    event.stopImmediatePropagation(); // Prevent duplicate handling from multiple listeners
  }

  /**
   * Handle keyboard shortcuts
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    // Don't handle if typing in an input
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
      return;
    }

    // Undo/Redo — works in all edit modes (not just cube editor)
    if (event.ctrlKey || event.metaKey) {
      // Don't undo during an active drag
      if (this.activeTool && this.activeTool.isDragging) return;

      if (event.key === 'z' && !event.shiftKey) {
        if (this.undoManager && this.undoManager.canUndo()) {
          event.preventDefault();
          this.undoManager.undo();
        }
        return;
      }
      if (event.key === 'y' || (event.key === 'z' && event.shiftKey) || (event.key === 'Z' && event.shiftKey)) {
        if (this.undoManager && this.undoManager.canRedo()) {
          event.preventDefault();
          this.undoManager.redo();
        }
        return;
      }
    }

    // Toggle editor with Tab
    if (event.key === 'Tab') {
      event.preventDefault();
      this.toggle();
      return;
    }

    // Only handle shortcuts when editor is enabled
    if (!this.enabled) return;

    switch (event.key) {
      case '1':
        this.setTool('add');
        break;
      case '2':
        this.setTool('move');
        break;
      case '3':
        this.setTool('extend');
        break;
      case 'Escape':
        this.setTool(null);
        break;
    }
  }

  /**
   * Update the editor (called from game loop)
   */
  update() {
    // Update ghost preview if visible
    if (this.ghostPreview.isVisible()) {
      this.ghostPreview.update();
    }
  }
}
